import { ReactNode, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { SubscriptionGate } from "@/components/SubscriptionGate";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";
import { Moon, Sun, LogOut, Loader2 } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import { useAuthContext } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { NotificationCenter } from "@/components/NotificationCenter";
import { QuickActions } from "@/components/QuickActions";
import { MobileLayout } from "@/components/mobile/MobileLayout";
import { TradeSwitcher } from "@/components/trades/TradeSwitcher";

interface AppLayoutProps {
  children: ReactNode;
}

export default function AppLayout({ children }: AppLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const { user, loading } = useAuthContext();
  const isMobile = useIsMobile();
  
  const userEmail = user?.email || "";

  const pageTitle = (() => {
    const path = location.pathname;
    if (path === "/") return "Home";
    const segment = path.split("/").filter(Boolean)[0] || "Workspace";
    return segment
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  })();

  // Redirect to auth if not logged in
  useEffect(() => {
    if (!loading && !user) {
      navigate("/auth");
    }
  }, [loading, user, navigate]);

  // Show loading state while auth is resolving
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Don't render layout if user is not authenticated
  if (!user) {
    return null;
  }

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch (err) {
      console.warn("Sign out error (clearing local session anyway):", err);
    }
    // Always clear local storage and redirect, even if server-side logout fails
    localStorage.removeItem("sb-uqcuajozfzvoiomyxnbg-auth-token");
    toast.success("Signed out successfully");
    navigate("/auth");
  };

  const getUserInitials = (email: string) => {
    return email.slice(0, 2).toUpperCase();
  };

  return (
    <SubscriptionGate>
      {isMobile ? (
        <MobileLayout>{children}</MobileLayout>
      ) : (
        <SidebarProvider defaultOpen={!isMobile}>
          <div className="app-shell flex min-h-screen w-full bg-background">
            <AppSidebar />
            
            <div className="app-shell-main flex-1 flex flex-col w-full min-w-0">
              {/* Top Navigation */}
              <header className="app-shell-header sticky top-0 z-30 border-b border-border bg-card/95">
                <div className="flex h-14 md:h-16 items-center gap-2 md:gap-4 px-3 md:px-6">
                  <SidebarTrigger className="app-shell-trigger rounded-xl transition-colors" />
                  
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-foreground md:text-base">{pageTitle}</div>
                    <div className="hidden text-xs text-muted-foreground md:block">Operations workspace</div>
                  </div>

                  <div className="flex-1" />

                  {/* Right Side Actions */}
                  <div className="flex items-center gap-1 md:gap-2">
                    <TradeSwitcher />
                    <QuickActions />
                    
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={toggleTheme}
                      className="app-shell-icon-button h-9 w-9 md:h-10 md:w-10 rounded-xl"
                    >
                      {theme === "light" ? (
                        <Moon className="w-4 h-4 md:w-5 md:h-5 text-neon-purple" />
                      ) : (
                        <Sun className="w-4 h-4 md:w-5 md:h-5 text-neon-orange" />
                      )}
                    </Button>

                    <NotificationCenter />

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="app-shell-icon-button rounded-xl h-9 w-9 md:h-10 md:w-10 p-0">
                          <Avatar className="h-9 w-9 md:h-10 md:w-10">
                            <AvatarFallback className="bg-muted text-foreground font-semibold text-xs md:text-sm">
                              {getUserInitials(userEmail)}
                            </AvatarFallback>
                          </Avatar>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56">
                        <DropdownMenuLabel>
                          <div className="flex flex-col">
                            <span className="text-sm font-medium">Account</span>
                            <span className="text-xs text-muted-foreground">{userEmail}</span>
                          </div>
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => navigate("/settings")}>
                          Settings
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={handleSignOut} className="text-destructive">
                          <LogOut className="w-4 h-4 mr-2" />
                          Sign Out
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </header>

              {/* Main Content */}
              <main className="flex-1 overflow-auto bg-background">
                <div className="w-full p-3 md:p-6 [&_.container]:mx-0 [&_.container]:w-full [&_.container]:max-w-none [&_.max-w-7xl]:max-w-none [&_.max-w-6xl]:max-w-none [&_.max-w-5xl]:max-w-none [&_.max-w-4xl]:max-w-none">
                  {children}
                </div>
              </main>
            </div>
          </div>
        </SidebarProvider>
      )}
    </SubscriptionGate>
  );
}
